import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FaArrowUp, FaArrowDown, FaEye, FaEyeSlash, FaGripLines, FaExpand, FaCompress, FaDownload } from 'react-icons/fa';
import { Button } from '../common';
import { Responsive, WidthProvider } from 'react-grid-layout';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

const ResponsiveGridLayout = WidthProvider(Responsive);

// Define KPI cards with their configurations
const KPI_CARDS = {
  salesRep: { name: 'Sales Rep Performance', defaultWidth: 2, defaultHeight: 4, minW: 2, minH: 2 },
  newLeads: { name: 'New Leads', defaultWidth: 1, defaultHeight: 3, minW: 1, minH: 2 },
  newDeals: { name: 'Deals Created', defaultWidth: 1, defaultHeight: 3, minW: 1, minH: 2 },
  avgDealValue: { name: 'Avg Won Deal Value', defaultWidth: 1, defaultHeight: 3, minW: 1, minH: 2 },
  revenue: { name: 'Won Deal Revenue', defaultWidth: 1, defaultHeight: 3, minW: 1, minH: 2 },
  courses: { name: 'Lessons', defaultWidth: 1, defaultHeight: 3, minW: 1, minH: 2 },
  studentsPerLesson: { name: 'Students per Lesson', defaultWidth: 1, defaultHeight: 4, minW: 1, minH: 2 },
  progressionRate: { name: 'Progression Rate', defaultWidth: 1, defaultHeight: 4, minW: 1, minH: 2 },
  refunded: { name: 'Refunded', defaultWidth: 1, defaultHeight: 4, minW: 1, minH: 2 },
  adSpend: { name: 'Ad Spend', defaultWidth: 1, defaultHeight: 4, minW: 1, minH: 2 },
  roas: { name: 'ROAS', defaultWidth: 2, defaultHeight: 5, minW: 2, minH: 3 },
  cpl: { name: 'Cost Per Lead', defaultWidth: 1, defaultHeight: 3, minW: 1, minH: 2 }
};

const KPIsView = () => {
  const [kpiData, setKpiData] = useState({
    newLeads: { current: 0, comparison: 0, percentChange: 0 },
    newDeals: { current: 0, comparison: 0, percentChange: 0 },
    avgDealValue: {
      current: { value: 0, byCountry: {} },
      comparison: { value: 0, byCountry: {} },
      percentChange: 0,
      currency: 'NOK'
    },
    revenue: { current: 0, comparison: 0, percentChange: 0, currency: 'NOK' },
    refunded: { 
      current: { total: 0, byCountry: {} }, 
      comparison: { total: 0, byCountry: {} }, 
      percentChange: 0,
      currency: 'NOK'
    },
    courses: { 
      current: { total: 0, byCountry: {} }, 
      comparison: { total: 0, byCountry: {} }, 
      percentChange: 0 
    },
    salesReps: {
      current: { bySalesRep: {} },
      currency: 'NOK'
    },
    adSpend: {
      current: { total: 0, bySource: {}, byCampaign: {}, byAdSet: {} },
      comparison: { total: 0, bySource: {}, byCampaign: {}, byAdSet: {} },
      percentChange: 0,
      currency: 'NOK'
    },
    roas: {
      current: 0,
      comparison: 0,
      percentChange: 0
    },
    cpl: {
      current: 0,
      comparison: 0,
      percentChange: 0,
      currency: 'NOK'
    }
  });
  const [dateRanges, setDateRanges] = useState({
    current: { start: '', end: '' },
    comparison: { start: '', end: '' }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Load preferences from localStorage or use defaults
  const [period, setPeriod] = useState(() => {
    return localStorage.getItem('kpi_period') || 'this_month';
  });
  
  const [comparison, setComparison] = useState(() => {
    return localStorage.getItem('kpi_comparison') || 'previous';
  });
  
  const [showComparison, setShowComparison] = useState(() => {
    const saved = localStorage.getItem('kpi_show_comparison');
    return saved === null ? true : saved === 'true';
  });
  const [refreshing, setRefreshing] = useState(false);
  const [adDataFreshness, setAdDataFreshness] = useState({
    lastRefreshed: null,
    google: {},
    meta: {}
  });

  // State for layout and visible cards
  const [layouts, setLayouts] = useState(() => {
    const saved = localStorage.getItem('kpi_layouts');
    return saved ? JSON.parse(saved) : {};
  });
  
  const [visibleCards, setVisibleCards] = useState(() => {
    const saved = localStorage.getItem('kpi_visible_cards');
    if (saved) {
      return JSON.parse(saved);
    }
    // Default visibility - tutoring-related cards are deactivated by default
    return Object.keys(KPI_CARDS).reduce((acc, key) => {
      // Deactivate tutoring-specific cards by default
      if (key === 'courses' || key === 'studentsPerLesson' || key === 'progressionRate') {
        acc[key] = false;
      } else {
        acc[key] = true;
      }
      return acc;
    }, {});
  });
  
  const [showCustomizeModal, setShowCustomizeModal] = useState(false);
  const [presentationMode, setPresentationMode] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const gridRef = useRef(null);

  // Track component mount/unmount
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  // Effect for initial component mount
  useEffect(() => {
    const initialSetup = async () => {
      console.log("Component mounted, initial setup...");
      
      // First get the freshness status
      await fetchAdDataFreshness();
      
      // Then fetch the KPI data
      await fetchKpiData();
      
      // Mark initial load as complete
      setIsInitialLoad(false);
      
      console.log("Initial setup complete");
    };
    
    initialSetup();
    
    // Cleanup function
    return () => {
      console.log("Component unmounted");
    };
  }, []);
  
  // Effect for when filters change (but not on initial mount)
  useEffect(() => {
    if (!isInitialLoad) {
      console.log("Filters changed, fetching KPI data...");
      fetchKpiData();
    }
  }, [period, comparison, showComparison, isInitialLoad]);
  
  // Effect to check if we need to refresh data when period changes
  // This only runs when the period changes, not on every component mount
  useEffect(() => {
    // Skip the initial mount check - we'll handle that separately
    if (isInitialLoad) return;
    
    const checkAndRefreshAdData = async () => {
      // First fetch the latest freshness data
      await fetchAdDataFreshness();
      
      // Wait for the freshness data to be loaded
      if (!adDataFreshness || !adDataFreshness.google || !adDataFreshness.meta) {
        return;
      }
      
      // Map period to the internal period format
      const refreshPeriod = mapPeriodToRefreshPeriod(period);
      
      // Check if either Google or Meta data is missing or stale for this period
      const googleFresh = adDataFreshness.google[refreshPeriod]?.fresh;
      const metaFresh = adDataFreshness.meta[refreshPeriod]?.fresh;
      
      // Check if data was recently refreshed (within the last 5 minutes)
      const isRecentlyRefreshed = adDataFreshness.lastRefreshed && 
        (Date.now() - adDataFreshness.lastRefreshed < 5 * 60 * 1000); // 5 minutes
      
      console.log(`Period ${period} (${refreshPeriod}) - Google fresh: ${googleFresh}, Meta fresh: ${metaFresh}, Recently refreshed: ${isRecentlyRefreshed}`);
      
      // Only refresh if data is stale AND not recently refreshed
      if ((!googleFresh || !metaFresh) && !isRecentlyRefreshed) {
        console.log(`Auto-refreshing ad data for period: ${refreshPeriod}`);
        await refreshAdSpendData();
      } else if (isRecentlyRefreshed) {
        console.log('Skipping auto-refresh: data was recently refreshed');
      } else {
        console.log('Skipping auto-refresh: data is fresh');
      }
    };
    
    checkAndRefreshAdData();
  }, [period]);
  
  // Fetch ad data freshness status
  const fetchAdDataFreshness = async () => {
    try {
      const token = localStorage.getItem('token');
      console.log('Fetching ad data freshness...');
      const response = await axios.get('/api/ad-spend-refresh/status', {
        headers: { 'x-auth-token': token }
      });
      
      console.log('Ad data freshness response:', response.data);
      
      if (response.data.success) {
        console.log('Setting ad data freshness:', response.data.freshness);
        setAdDataFreshness(response.data.freshness);
      } else {
        console.warn('Freshness data not successful:', response.data);
      }
    } catch (err) {
      console.error('Error fetching ad data freshness:', err);
    }
  };

  const fetchKpiData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(
        '/api/kpis?period=' + period + (showComparison ? '&comparison=' + comparison : ''), 
        { headers: { 'x-auth-token': token } }
      );
      
      setKpiData(response.data.kpiData);
      setDateRanges(response.data.dateRanges);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching KPI data:', err);
      setError('Failed to load KPI data. Please try again.');
      setLoading(false);
    }
  };
  
  // Refresh ad spend data
  const refreshAdSpendData = async (source = null) => {
    try {
      setRefreshing(true);
      const token = localStorage.getItem('token');
      
      // Map KPI period to ad spend refresh period
      const refreshPeriod = mapPeriodToRefreshPeriod(period);
      
      // Request body
      const requestBody = {
        period: refreshPeriod,
        forceRefresh: true
      };
      
      // If a specific source is specified (google or meta)
      if (source) {
        requestBody.source = source;
      }
      
      // Make the request
      const response = await axios.post(
        '/api/ad-spend-refresh/refresh',
        requestBody,
        { headers: { 'x-auth-token': token } }
      );
      
      if (response.data.success) {
        // Refresh the KPI data
        await fetchKpiData();
        // Also refresh the freshness status
        await fetchAdDataFreshness();
      }
      
      setRefreshing(false);
    } catch (err) {
      console.error('Error refreshing ad spend data:', err);
      setRefreshing(false);
    }
  };

  const formatCurrency = (value, currency) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'NOK',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const renderPercentChange = (percentChange) => {
    if (percentChange === 0) return <span className="text-gray-500">0%</span>;
    
    if (percentChange > 0) {
      return (
        <span className="text-green-600 flex items-center">
          <FaArrowUp className="mr-1" /> {percentChange.toFixed(1)}%
        </span>
      );
    } else {
      return (
        <span className="text-red-600 flex items-center">
          <FaArrowDown className="mr-1" /> {Math.abs(percentChange).toFixed(1)}%
        </span>
      );
    }
  };
  
  // Special function for CPL where the arrows should be reversed
  // but the colors remain the same (red = bad, green = good)
  const renderCplPercentChange = (percentChange) => {
    if (percentChange === 0) return <span className="text-gray-500">0%</span>;
    
    if (percentChange > 0) {
      // For CPL, positive change means cost decreased (good)
      return (
        <span className="text-green-600 flex items-center">
          <FaArrowDown className="mr-1" /> {percentChange.toFixed(1)}%
        </span>
      );
    } else {
      // For CPL, negative change means cost increased (bad)
      return (
        <span className="text-red-600 flex items-center">
          <FaArrowUp className="mr-1" /> {Math.abs(percentChange).toFixed(1)}%
        </span>
      );
    }
  };
  
  // Special function for metrics where increase is bad (like Refunded)
  // For these, we keep the arrow direction but invert the colors
  const renderInvertedColorPercentChange = (percentChange) => {
    if (percentChange === 0) return <span className="text-gray-500">0%</span>;
    
    if (percentChange > 0) {
      // For Refunded, positive change means increase (bad)
      return (
        <span className="text-red-600 flex items-center">
          <FaArrowUp className="mr-1" /> {percentChange.toFixed(1)}%
        </span>
      );
    } else {
      // For Refunded, negative change means decrease (good)
      return (
        <span className="text-green-600 flex items-center">
          <FaArrowDown className="mr-1" /> {Math.abs(percentChange).toFixed(1)}%
        </span>
      );
    }
  };

  // Format a timestamp to a readable time string
  const formatLastRefreshed = (timestamp) => {
    if (!timestamp) return 'Never';
    
    // Handle potentially invalid timestamps
    let date;
    try {
      date = new Date(timestamp);
      // Check for Invalid Date
      if (isNaN(date.getTime())) {
        console.warn('Invalid timestamp:', timestamp);
        return 'Unknown';
      }
    } catch (e) {
      console.error('Error parsing timestamp:', e);
      return 'Unknown';
    }
    
    const now = new Date();
    const diffMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffMinutes < 60) {
      return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
    } else if (diffMinutes < 1440) {
      const hours = Math.floor(diffMinutes / 60);
      return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleString();
    }
  };
  
  // Map the KPI period to the internal refresh period format
  const mapPeriodToRefreshPeriod = (periodValue) => {
    switch (periodValue) {
      case 'today':
        return 'today';
      case 'yesterday':
        return 'yesterday';
      case 'this_month':
      case 'this_year':
      case 'this_week':
      case 'last_7_days':
      case 'last_30_days':
        return 'thisMonth';
      case 'last_month':
        return 'lastMonth';
      case 'last_year':
      case 'last_week':
      case 'last_90_days':
        return 'older';
      default:
        return 'thisMonth';
    }
  };
  
  // Determine if data is fresh or stale
  const getDataFreshnessClass = (periodValue) => {
    const periodType = mapPeriodToRefreshPeriod(periodValue);
    
    if (!adDataFreshness || !adDataFreshness.google[periodType] || !adDataFreshness.meta[periodType]) {
      return 'text-gray-500'; // Unknown status
    }
    
    const googleFresh = adDataFreshness.google[periodType].fresh;
    const metaFresh = adDataFreshness.meta[periodType].fresh;
    
    if (googleFresh && metaFresh) {
      return 'text-green-600'; // Both fresh
    } else if (!googleFresh && !metaFresh) {
      return 'text-red-600'; // Both stale
    } else {
      return 'text-yellow-600'; // Mixed
    }
  };
  
  // Generate default layouts for all breakpoints
  const generateDefaultLayouts = () => {
    const defaultLayouts = { lg: [], md: [], sm: [], xs: [], xxs: [] };
    let x = 0;
    let y = 0;
    
    Object.entries(KPI_CARDS).forEach(([key, config]) => {
      if (visibleCards[key]) {
        const item = {
          i: key,
          x: x,
          y: y,
          w: config.defaultWidth,
          h: config.defaultHeight,
          minW: config.minW,
          minH: config.minH
        };
        
        // Add to all breakpoints with adjusted sizes
        defaultLayouts.lg.push(item);
        defaultLayouts.md.push({ ...item, w: Math.min(config.defaultWidth, 2), minW: config.minW, minH: config.minH });
        defaultLayouts.sm.push({ ...item, w: 1, x: 0, minW: 1, minH: config.minH });
        defaultLayouts.xs.push({ ...item, w: 1, x: 0, minW: 1, minH: config.minH });
        defaultLayouts.xxs.push({ ...item, w: 1, x: 0, minW: 1, minH: config.minH });
        
        // Update position for next item
        x += config.defaultWidth;
        if (x >= 5) {
          x = 0;
          y += config.defaultHeight;
        }
      }
    });
    
    return defaultLayouts;
  };
  
  // Handle layout changes
  const handleLayoutChange = (layout, allLayouts) => {
    setLayouts(allLayouts);
    localStorage.setItem('kpi_layouts', JSON.stringify(allLayouts));
  };
  
  // Toggle card visibility
  const toggleCardVisibility = (cardKey) => {
    const newVisibleCards = { ...visibleCards, [cardKey]: !visibleCards[cardKey] };
    setVisibleCards(newVisibleCards);
    localStorage.setItem('kpi_visible_cards', JSON.stringify(newVisibleCards));
  };
  
  // Reset to default layout
  const resetLayout = () => {
    const defaultLayouts = generateDefaultLayouts();
    setLayouts(defaultLayouts);
    localStorage.setItem('kpi_layouts', JSON.stringify(defaultLayouts));
  };

  // Download KPIs as PDF
  const downloadPDF = async () => {
    setDownloading(true);
    
    try {
      // Create a wrapper element for better capture
      const wrapper = document.createElement('div');
      wrapper.style.position = 'fixed';
      wrapper.style.top = '0';
      wrapper.style.left = '0';
      wrapper.style.width = '100vw';
      wrapper.style.height = '100vh';
      wrapper.style.backgroundColor = '#ffffff';
      wrapper.style.zIndex = '-9999';
      wrapper.style.padding = '40px';
      
      // Create title elements
      const title = document.createElement('h1');
      title.textContent = 'Key Performance Indicators';
      title.style.fontSize = '24px';
      title.style.fontWeight = 'bold';
      title.style.textAlign = 'center';
      title.style.marginBottom = '10px';
      
      const dateRange = document.createElement('p');
      dateRange.textContent = `${dateRanges.current.start} - ${dateRanges.current.end}`;
      if (showComparison) {
        dateRange.textContent += ` (compared to ${dateRanges.comparison.start} - ${dateRanges.comparison.end})`;
      }
      dateRange.style.fontSize = '16px';
      dateRange.style.textAlign = 'center';
      dateRange.style.marginBottom = '30px';
      dateRange.style.color = '#666';
      
      // Clone only the grid content
      const gridElement = document.querySelector('.react-grid-layout');
      if (!gridElement) {
        throw new Error('Grid not found');
      }
      
      const clonedGrid = gridElement.cloneNode(true);
      clonedGrid.style.minHeight = '600px';
      
      // Append elements to wrapper
      wrapper.appendChild(title);
      wrapper.appendChild(dateRange);
      wrapper.appendChild(clonedGrid);
      
      // Append to body
      document.body.appendChild(wrapper);
      
      // Wait for render
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Create canvas
      const canvas = await html2canvas(wrapper, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
        useCORS: true,
        allowTaint: true
      });
      
      // Remove wrapper
      document.body.removeChild(wrapper);
      
      if (!canvas || canvas.width === 0 || canvas.height === 0) {
        throw new Error('Failed to create canvas');
      }

      // Create PDF
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const titleHeight = 35; // Space for title and date

      // Add title and date
      pdf.setFontSize(20);
      pdf.text('Key Performance Indicators', pageWidth / 2, 15, { align: 'center' });
      
      pdf.setFontSize(12);
      const dateText = `${dateRanges.current.start} - ${dateRanges.current.end}`;
      const comparisonText = showComparison 
        ? ` (compared to ${dateRanges.comparison.start} - ${dateRanges.comparison.end})`
        : '';
      pdf.text(dateText + comparisonText, pageWidth / 2, 25, { align: 'center' });

      // Calculate image dimensions
      const imgWidth = pageWidth - 20; // 10mm margins on each side
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // Check if we need multiple pages
      const availableHeight = pageHeight - titleHeight - 10; // 10mm bottom margin
      
      if (imgHeight <= availableHeight) {
        // Fits on one page
        try {
          const imgData = canvas.toDataURL('image/jpeg', 0.95);
          pdf.addImage(imgData, 'JPEG', 10, titleHeight, imgWidth, imgHeight);
        } catch (imgError) {
          console.error('Error adding image:', imgError);
          throw new Error('Failed to add image to PDF');
        }
      } else {
        // Multiple pages needed
        const scale = imgWidth / canvas.width;
        let currentY = 0;
        let pageNum = 0;
        
        while (currentY < canvas.height) {
          if (pageNum > 0) {
            pdf.addPage();
          }
          
          const startY = pageNum === 0 ? titleHeight : 10;
          const availablePageHeight = pageHeight - startY - 10;
          const remainingHeight = canvas.height - currentY;
          const heightToDraw = Math.min(remainingHeight, availablePageHeight / scale);
          
          // Create a temporary canvas for this page section
          const tempCanvas = document.createElement('canvas');
          tempCanvas.width = canvas.width;
          tempCanvas.height = heightToDraw;
          const tempCtx = tempCanvas.getContext('2d');
          
          tempCtx.drawImage(
            canvas,
            0, currentY, canvas.width, heightToDraw,
            0, 0, canvas.width, heightToDraw
          );
          
          try {
            const tempImgData = tempCanvas.toDataURL('image/jpeg', 0.95);
            pdf.addImage(tempImgData, 'JPEG', 10, startY, imgWidth, heightToDraw * scale);
          } catch (imgError) {
            console.error('Error adding page image:', imgError);
          }
          
          currentY += heightToDraw;
          pageNum++;
        }
      }

      // Download the PDF
      const today = new Date().toISOString().split('T')[0];
      pdf.save(`KPIs-${today}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert(`Failed to generate PDF: ${error.message}. Please try again.`);
    } finally {
      setDownloading(false);
    }
  };
  
  const renderTimeFilter = () => {
    return (
      <div className="flex flex-col md:flex-row md:items-center space-y-2 md:space-y-0 md:space-x-4 mb-6 bg-white rounded-lg shadow p-4">
        <div className="flex flex-col">
          <label htmlFor="period" className="text-sm font-medium text-gray-700 mb-1">
            Time Period
          </label>
          <select 
            id="period"
            className="border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            value={period}
            onChange={(e) => {
              const value = e.target.value;
              setPeriod(value);
              localStorage.setItem('kpi_period', value);
            }}
          >
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="this_week">This Week</option>
            <option value="last_week">Last Week</option>
            <option value="this_month">This Month</option>
            <option value="last_month">Last Month</option>
            <option value="this_year">This Year</option>
            <option value="last_year">Last Year</option>
            <option value="last_7_days">Last 7 Days</option>
            <option value="last_30_days">Last 30 Days</option>
            <option value="last_90_days">Last 90 Days</option>
          </select>
        </div>
        
        {showComparison && (
          <div className="flex flex-col">
            <label htmlFor="comparison" className="text-sm font-medium text-gray-700 mb-1">
              Compare To
            </label>
            <select 
              id="comparison"
              className="border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              value={comparison}
              onChange={(e) => {
                const value = e.target.value;
                setComparison(value);
                localStorage.setItem('kpi_comparison', value);
              }}
              disabled={period === 'last_year'}
            >
              <option value="previous">Previous Period</option>
              <option value="last_year">Same Period Last Year</option>
            </select>
          </div>
        )}
        
        <div className="flex items-center ml-4 self-end">
          <input
            id="showComparison"
            type="checkbox"
            checked={showComparison}
            onChange={(e) => {
              const value = e.target.checked;
              setShowComparison(value);
              localStorage.setItem('kpi_show_comparison', value);
            }}
            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
          />
          <label htmlFor="showComparison" className="ml-2 text-sm font-medium text-gray-700">
            Show Comparison
          </label>
        </div>
        
        <div className="text-sm text-gray-500 italic flex-grow">
          {!loading && (
            <div>
              <div>Current: {dateRanges.current.start} - {dateRanges.current.end}</div>
              {showComparison && dateRanges.comparison.start && (
                <div>Compared to: {dateRanges.comparison.start} - {dateRanges.comparison.end}</div>
              )}
            </div>
          )}
        </div>
        
        {/* Refresh Ad Data Button with Freshness Indicator - Far right */}
        <div className="flex-shrink-0 flex flex-col items-center ml-2">
          <button
            onClick={() => refreshAdSpendData()}
            disabled={refreshing}
            className={`p-2 rounded-full transition-all duration-300 ${
              refreshing 
                ? 'bg-gray-200 text-gray-500 cursor-wait' 
                : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-indigo-500'
            }`}
            title={refreshing ? 'Refreshing data...' : 'Refresh ad spend data'}
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              className={`h-5 w-5 ${refreshing ? 'animate-spin' : ''}`} 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" 
              />
            </svg>
          </button>
          
          {/* Data Freshness Indicator */}
          <div className="text-xs text-gray-500 mt-1 whitespace-nowrap">
            <span>Updated </span>
            <span className={adDataFreshness?.lastRefreshed ? getDataFreshnessClass(period) : "text-gray-500"}>
              {adDataFreshness?.lastRefreshed 
                ? formatLastRefreshed(adDataFreshness.lastRefreshed)
                : refreshing ? "Refreshing..." : "Just now"}
            </span>
          </div>
        </div>
        
        {/* Customize, Present, and Download Buttons */}
        <div className="flex-shrink-0 flex space-x-2">
          <button
            onClick={() => setShowCustomizeModal(true)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            Customize
          </button>
          <button
            onClick={() => setPresentationMode(true)}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 flex items-center"
          >
            <FaExpand className="mr-2" />
            Present
          </button>
          <button
            onClick={downloadPDF}
            disabled={downloading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FaDownload className="mr-2" />
            {downloading ? 'Generating...' : 'Download PDF'}
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-4">
        <h3 className="text-lg font-semibold mb-4">Key Performance Indicators</h3>
        <div className="animate-pulse">
          <div className="h-12 bg-gray-200 rounded mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
            <div className="h-64 bg-gray-200 rounded lg:col-span-1 xl:col-span-2"></div>
            <div className="h-40 bg-gray-200 rounded"></div>
            <div className="h-40 bg-gray-200 rounded"></div>
            <div className="h-40 bg-gray-200 rounded"></div>
            <div className="h-40 bg-gray-200 rounded"></div>
            <div className="h-40 bg-gray-200 rounded"></div>
            <div className="h-40 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <h3 className="text-lg font-semibold mb-4">Key Performance Indicators</h3>
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <p>{error}</p>
          <Button 
            onClick={fetchKpiData}
            variant="danger"
            className="mt-2 py-1 px-3"
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  // Card wrapper component with drag handle
  const CardWrapper = ({ children, title }) => (
    <div className="bg-white rounded-lg shadow p-3 h-full flex flex-col overflow-hidden">
      <div className="flex items-center justify-between mb-2 flex-shrink-0">
        <h4 className="text-lg font-medium text-gray-700 truncate">{title}</h4>
        <FaGripLines className="text-gray-400 cursor-move drag-handle ml-2 flex-shrink-0" />
      </div>
      <div className="flex-1 overflow-auto min-h-0">
        {children}
      </div>
    </div>
  );

  // Generic KPI Card for simple metrics
  const renderSimpleKpiCard = (key, title, data, renderChange = renderPercentChange) => (
    <CardWrapper title={title}>
      <div className="flex items-baseline flex-wrap pr-1">
        <span className="text-3xl font-bold mr-2">
          {typeof data?.current === 'object' && data?.current?.total !== undefined 
            ? data.current.total 
            : (typeof data?.current === 'number' ? data.current : 0)}
        </span>
        {showComparison && (
          <span className="mt-1">
            {renderChange(data?.percentChange || 0)}
          </span>
        )}
      </div>
      {showComparison && (typeof data?.comparison === 'object' ? data?.comparison?.total : data?.comparison) > 0 && (
        <p className="text-sm text-gray-500 mt-2">
          vs {typeof data?.comparison === 'object' ? data.comparison.total : data.comparison} in previous period
        </p>
      )}
      
      {/* Country Breakdown */}
      {data?.current?.byCountry && 
       Object.entries(data.current.byCountry)
        .filter(([country]) => country !== 'Unspecified')
        .length > 0 && (
        <div className="mt-4 pt-3 border-t border-gray-200">
          <h5 className="text-sm font-medium text-gray-700 mb-2">By Country:</h5>
          <ul className="text-sm">
            {Object.entries(data.current.byCountry)
              .filter(([country]) => country !== 'Unspecified')
              .sort((a, b) => b[1] - a[1])
              .map(([country, count]) => (
                <li key={country} className="flex justify-between items-center mb-1">
                  <span>{country === 'Norway' ? '🇳🇴' : country === 'Sweden' ? '🇸🇪' : country === 'Germany' ? '🇩🇪' : country === 'Switzerland' ? '🇨🇭' : ''} {country}</span>
                  <span className="font-medium">{count}</span>
                </li>
              ))}
          </ul>
        </div>
      )}
    </CardWrapper>
  );

  // Sales Rep Performance Card
  const renderSalesRepCard = () => (
    <CardWrapper title="Sales Rep Performance">
      <div className="overflow-y-auto max-h-64">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sales Rep</th>
              <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Cash Collected</th>
              <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Deals</th>
              <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Conv. Rate</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {kpiData.salesReps?.current?.bySalesRep && 
              Object.entries(kpiData.salesReps.current.bySalesRep)
                .sort((a, b) => b[1].value - a[1].value)
                .map(([salesRep, data]) => (
                  <tr key={salesRep}>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">{salesRep}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                      {formatCurrency(data.value, kpiData.salesReps.currency)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                      {data.dealsCount || 0}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                      {data.conversionRate}%
                    </td>
                  </tr>
                ))
            }
            {(!kpiData.salesReps?.current?.bySalesRep || Object.keys(kpiData.salesReps.current.bySalesRep).length === 0) && (
              <tr>
                <td colSpan="4" className="px-3 py-2 text-sm text-gray-500 text-center">No data available</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </CardWrapper>
  );

  // Map of card keys to their render functions
  const cardRenderers = {
    salesRep: renderSalesRepCard,
    newLeads: () => renderSimpleKpiCard('newLeads', 'New Leads', kpiData.newLeads),
    newDeals: () => renderSimpleKpiCard('newDeals', 'Deals Created', kpiData.newDeals),
    avgDealValue: () => (
      <CardWrapper title="Avg Won Deal Value">
        <div className="flex items-baseline flex-wrap pr-1">
          <span className="text-3xl font-bold mr-2">
            {formatCurrency(kpiData.avgDealValue?.current?.value || 0, kpiData.avgDealValue?.currency)}
          </span>
          {showComparison && (
            <span className="mt-1">
              {renderPercentChange(kpiData.avgDealValue?.percentChange || 0)}
            </span>
          )}
        </div>
        {showComparison && kpiData.avgDealValue?.comparison?.value > 0 && (
          <p className="text-sm text-gray-500 mt-2">
            vs {formatCurrency(kpiData.avgDealValue.comparison.value, kpiData.avgDealValue.currency)} in previous period
          </p>
        )}
      </CardWrapper>
    ),
    revenue: () => (
      <CardWrapper title="Won Deal Revenue">
        <div className="flex items-baseline flex-wrap pr-1">
          <span className="text-3xl font-bold mr-2">
            {formatCurrency(
              typeof kpiData.revenue?.current === 'object' && kpiData.revenue?.current?.total !== undefined
                ? kpiData.revenue.current.total 
                : (typeof kpiData.revenue?.current === 'number' ? kpiData.revenue.current : 0), 
              kpiData.revenue?.currency
            )}
          </span>
          {showComparison && (
            <span className="mt-1">
              {renderPercentChange(kpiData.revenue.percentChange)}
            </span>
          )}
        </div>
        {showComparison && (
          typeof kpiData.revenue?.comparison === 'object' 
            ? kpiData.revenue?.comparison?.total 
            : kpiData.revenue?.comparison
        ) > 0 && (
          <p className="text-sm text-gray-500 mt-2">
            vs {formatCurrency(
              typeof kpiData.revenue?.comparison === 'object' 
                ? kpiData.revenue.comparison.total 
                : kpiData.revenue.comparison, 
              kpiData.revenue?.currency
            )} in previous period
          </p>
        )}
      </CardWrapper>
    ),
    courses: () => renderSimpleKpiCard('courses', 'Lessons', kpiData.courses),
    studentsPerLesson: () => (
      <CardWrapper title="Students per Lesson">
        <div className="flex items-baseline flex-wrap pr-1">
          <span className="text-3xl font-bold mr-2" style={{ color: (kpiData.studentsPerLesson?.current?.average || 0) >= 7 ? '#047857' : '#B91C1C' }}>
            {kpiData.studentsPerLesson?.current?.average || 0}
          </span>
          {showComparison && (
            <span className="mt-1">
              {renderPercentChange(kpiData.studentsPerLesson?.percentChange || 0)}
            </span>
          )}
        </div>
        {showComparison && kpiData.studentsPerLesson?.comparison?.average > 0 && (
          <p className="text-sm text-gray-500 mt-2">
            vs {kpiData.studentsPerLesson.comparison.average} in previous period
          </p>
        )}
        
        {/* Gauge for Students per Lesson */}
        <div className="mt-4 pt-3 border-t border-gray-200">
          <div className="mt-3">
            <div className="text-sm flex justify-between mb-1">
              <span className="text-gray-600">0</span>
              <span className="text-gray-600">7</span>
              <span className="text-gray-600">9+</span>
            </div>
            <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full rounded-full transition-all duration-500 ease-in-out"
                style={{
                  width: `${Math.min(Math.max((kpiData.studentsPerLesson?.current?.average || 0) / 9, 0) * 100, 100)}%`,
                  background: 'linear-gradient(90deg, #EF4444 0%, #F59E0B 50%, #047857 100%)'
                }}
              ></div>
            </div>
            <div className="text-xs text-gray-500 mt-1 flex justify-between">
              <span>Low</span>
              <span>Good</span>
              <span>Excellent</span>
            </div>
          </div>
        </div>
      </CardWrapper>
    ),
    progressionRate: () => (
      <CardWrapper title="Progression Rate">
        <div className="flex items-baseline flex-wrap pr-1">
          <span className="text-3xl font-bold mr-2" style={{ color: (kpiData.progressionRate?.current?.rate || 0) >= 60 ? '#047857' : '#B91C1C' }}>
            {(kpiData.progressionRate?.current?.rate || 0)}%
          </span>
          {showComparison && (
            <span className="mt-1">
              {renderPercentChange(kpiData.progressionRate?.percentChange || 0)}
            </span>
          )}
        </div>
        <p className="text-sm text-gray-600 mt-1">Level 1 to Level 2 (Live)</p>
        {showComparison && kpiData.progressionRate?.comparison?.rate > 0 && (
          <p className="text-sm text-gray-500 mt-2">
            vs {kpiData.progressionRate.comparison.rate}% in previous period
          </p>
        )}
        
        {/* Gauge for Progression Rate */}
        <div className="mt-4 pt-3 border-t border-gray-200">
          <div className="mt-3">
            <div className="text-sm flex justify-between mb-1">
              <span className="text-gray-600">0%</span>
              <span className="text-gray-600">60%</span>
              <span className="text-gray-600">100%</span>
            </div>
            <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full rounded-full transition-all duration-500 ease-in-out"
                style={{
                  width: `${Math.min(Math.max((kpiData.progressionRate?.current?.rate || 0) / 100, 0) * 100, 100)}%`,
                  background: 'linear-gradient(90deg, #EF4444 0%, #F59E0B 50%, #047857 100%)'
                }}
              ></div>
            </div>
            <div className="text-xs text-gray-500 mt-1 flex justify-between">
              <span>Poor</span>
              <span>Good</span>
              <span>Excellent</span>
            </div>
          </div>
        </div>
      </CardWrapper>
    ),
    refunded: () => (
      <CardWrapper title="Refunded">
        <div className="flex items-baseline flex-wrap pr-1">
          <span className="text-3xl font-bold text-red-600 mr-2">
            {formatCurrency(kpiData.refunded?.current?.total || 0, kpiData.refunded?.currency)}
          </span>
          {showComparison && (
            <span className="mt-1">
              {renderInvertedColorPercentChange(kpiData.refunded?.percentChange || 0)}
            </span>
          )}
        </div>
        
        {/* Refund Percentage */}
        <div className="text-sm text-red-600 font-medium">
          {kpiData.refunded?.current?.percentage > 0 && (
            <span>{kpiData.refunded.current.percentage}% of revenue</span>
          )}
        </div>
        
        {showComparison && kpiData.refunded?.comparison?.total > 0 && (
          <p className="text-sm text-gray-500 mt-2">
            vs {formatCurrency(kpiData.refunded.comparison.total, kpiData.refunded.currency)} 
            {kpiData.refunded?.comparison?.percentage > 0 && (
              <span> ({kpiData.refunded.comparison.percentage}%)</span>
            )} in previous period
          </p>
        )}
      </CardWrapper>
    ),
    adSpend: () => (
      <CardWrapper title="Ad Spend">
        <div className="flex items-baseline flex-wrap pr-1">
          <span className="text-3xl font-bold text-orange-600 mr-2">
            {formatCurrency(kpiData.adSpend?.current?.total || 0, kpiData.adSpend?.currency)}
          </span>
          {showComparison && (
            <span className="mt-1">
              {renderInvertedColorPercentChange(kpiData.adSpend?.percentChange || 0)}
            </span>
          )}
        </div>
        {showComparison && kpiData.adSpend?.comparison?.total > 0 && (
          <p className="text-sm text-gray-500 mt-2">
            vs {formatCurrency(kpiData.adSpend.comparison.total, kpiData.adSpend.currency)} in previous period
          </p>
        )}
        
        {/* Platform Breakdown */}
        {kpiData.adSpend?.current?.bySource && 
         Object.entries(kpiData.adSpend.current.bySource).length > 0 && (
          <div className="mt-4 pt-3 border-t border-gray-200">
            <h5 className="text-sm font-medium text-gray-700 mb-2">By Platform:</h5>
            <ul className="text-sm">
              {Object.entries(kpiData.adSpend.current.bySource)
                .sort((a, b) => b[1] - a[1])
                .map(([source, amount]) => (
                  <li key={source} className="flex justify-between items-center mb-1">
                    <span>
                      {source === 'google' ? 'Google Ads' : source === 'meta' ? 'Meta Ads' : source}
                    </span>
                    <span className="font-medium text-orange-600">{formatCurrency(amount, kpiData.adSpend.currency)}</span>
                  </li>
                ))}
            </ul>
          </div>
        )}

        {/* Campaign Breakdown */}
        {kpiData.adSpend?.current?.byCampaign && 
         Object.entries(kpiData.adSpend.current.byCampaign).length > 0 && (
          <div className="mt-4 pt-3 border-t border-gray-200">
            <h5 className="text-sm font-medium text-gray-700 mb-2">By Campaign:</h5>
            <ul className="text-sm max-h-32 overflow-y-auto">
              {Object.entries(kpiData.adSpend.current.byCampaign)
                .sort((a, b) => (b[1].spend || 0) - (a[1].spend || 0))
                .map(([campaign, data]) => {
                  const platformLabel = data.source === 'google' ? ' (Google)' : data.source === 'meta' ? ' (Meta)' : '';
                  return (
                    <li key={campaign} className="flex justify-between items-center mb-1">
                      <span className="truncate mr-2" title={campaign + platformLabel}>
                        {campaign}{platformLabel}
                      </span>
                      <span className="font-medium text-orange-600 flex-shrink-0">{formatCurrency(data.spend || 0, kpiData.adSpend.currency)}</span>
                    </li>
                  );
                })}
            </ul>
          </div>
        )}

        {/* Ad Set Breakdown */}
        {kpiData.adSpend?.current?.byAdSet && 
         Object.entries(kpiData.adSpend.current.byAdSet).length > 0 && (
          <div className="mt-4 pt-3 border-t border-gray-200">
            <h5 className="text-sm font-medium text-gray-700 mb-2">By Ad Set:</h5>
            <ul className="text-sm max-h-32 overflow-y-auto">
              {Object.entries(kpiData.adSpend.current.byAdSet)
                .sort((a, b) => (b[1].spend || 0) - (a[1].spend || 0))
                .map(([adSet, data]) => (
                  <li key={adSet} className="flex justify-between items-center mb-1">
                    <span className="truncate mr-2" title={adSet}>{adSet}</span>
                    <span className="font-medium text-orange-600 flex-shrink-0">{formatCurrency(data.spend || 0, kpiData.adSpend.currency)}</span>
                  </li>
                ))}
            </ul>
          </div>
        )}
      </CardWrapper>
    ),
    roas: () => (
      <CardWrapper title="ROAS">
        <div className="flex items-baseline flex-wrap pr-1">
          <span className="text-3xl font-bold mr-2" style={{ color: kpiData.roas?.current >= 3 ? '#047857' : '#B91C1C' }}>
            {kpiData.roas?.current ? kpiData.roas.current.toFixed(2) + 'x' : 'N/A'}
          </span>
          {showComparison && kpiData.roas?.percentChange !== 0 && (
            <span className="mt-1">
              {renderPercentChange(kpiData.roas?.percentChange || 0)}
            </span>
          )}
        </div>
        {showComparison && kpiData.roas?.comparison > 0 && (
          <p className="text-sm text-gray-500 mt-2">
            vs {kpiData.roas.comparison.toFixed(2)}x in previous period
          </p>
        )}
        
        <div className="mt-4 pt-3 border-t border-gray-200">
          <div className="mt-3">
            <div className="text-sm flex justify-between mb-1">
              <span className="text-gray-600">0</span>
              <span className="text-gray-600">3</span>
              <span className="text-gray-600">6+</span>
            </div>
            <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full rounded-full transition-all duration-500 ease-in-out"
                style={{
                  width: `${Math.min(Math.max((kpiData.roas?.current || 0) / 6, 0) * 100, 100)}%`,
                  background: 'linear-gradient(90deg, #EF4444 0%, #F59E0B 50%, #047857 100%)'
                }}
              ></div>
            </div>
            <div className="text-xs text-gray-500 mt-1 flex justify-between">
              <span>Poor</span>
              <span>Good</span>
              <span>Excellent</span>
            </div>
          </div>
        </div>
      </CardWrapper>
    ),
    cpl: () => (
      <CardWrapper title="Cost Per Lead">
        <div className="flex items-baseline flex-wrap pr-1">
          <span className="text-3xl font-bold mr-2" style={{ color: kpiData.cpl?.current >= 80 ? '#B91C1C' : '#047857' }}>
            {kpiData.cpl?.current ? formatCurrency(kpiData.cpl.current, kpiData.cpl.currency) : 'N/A'}
          </span>
          {showComparison && kpiData.cpl?.percentChange !== 0 && (
            <span className="mt-1">
              {renderCplPercentChange(kpiData.cpl?.percentChange || 0)}
            </span>
          )}
        </div>
        {showComparison && kpiData.cpl?.comparison > 0 && (
          <p className="text-sm text-gray-500 mt-2">
            vs {formatCurrency(kpiData.cpl.comparison, kpiData.cpl.currency)} in previous period
          </p>
        )}
        
        <div className="mt-4 pt-3 border-t border-gray-200">
          <div className="mt-3">
            <div className="text-sm flex justify-between mb-1">
              <span className="text-gray-600">0</span>
              <span className="text-gray-600">80</span>
              <span className="text-gray-600">160+</span>
            </div>
            <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full rounded-full transition-all duration-500 ease-in-out"
                style={{
                  width: `${Math.min(Math.max((kpiData.cpl?.current || 0) / 160, 0) * 100, 100)}%`,
                  background: 'linear-gradient(90deg, #047857 0%, #F59E0B 50%, #EF4444 100%)'
                }}
              ></div>
            </div>
            <div className="text-xs text-gray-500 mt-1 flex justify-between">
              <span>Excellent</span>
              <span>Good</span>
              <span>Poor</span>
            </div>
          </div>
        </div>
      </CardWrapper>
    )
  };

  // Render customize modal
  const renderCustomizeModal = () => {
    if (!showCustomizeModal) return null;
    
    return (
      <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
          <h3 className="text-lg font-semibold mb-4">Customize Dashboard</h3>
          
          <div className="space-y-2 mb-6">
            {Object.entries(KPI_CARDS).map(([key, config]) => (
              <div key={key} className="flex items-center justify-between">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={visibleCards[key]}
                    onChange={() => toggleCardVisibility(key)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded mr-2"
                  />
                  <span className="text-sm font-medium text-gray-700">{config.name}</span>
                </label>
              </div>
            ))}
          </div>
          
          <div className="flex justify-end space-x-2">
            <Button
              onClick={resetLayout}
              variant="secondary"
              className="py-2 px-4"
            >
              Reset Layout
            </Button>
            <Button
              onClick={() => setShowCustomizeModal(false)}
              variant="primary"
              className="py-2 px-4"
            >
              Done
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // Presentation mode rendering
  if (presentationMode) {
    return (
      <div className="fixed inset-0 bg-gray-100 z-50 overflow-auto">
        <div className="min-h-screen p-8">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h3 className="text-3xl font-semibold text-gray-900">Key Performance Indicators</h3>
              <p className="text-lg text-gray-600 mt-2">
                {dateRanges.current.start} - {dateRanges.current.end}
                {showComparison && (
                  <span className="ml-4">
                    (compared to {dateRanges.comparison.start} - {dateRanges.comparison.end})
                  </span>
                )}
              </p>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={downloadPDF}
                disabled={downloading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FaDownload className="mr-2" />
                {downloading ? 'Generating...' : 'Download PDF'}
              </button>
              <button
                onClick={() => setPresentationMode(false)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 flex items-center"
              >
                <FaCompress className="mr-2" />
                Exit
              </button>
            </div>
          </div>
          
          <div ref={gridRef} style={{ backgroundColor: '#ffffff', minHeight: '400px', padding: '16px' }}>
            <ResponsiveGridLayout
              className="layout"
              layouts={Object.keys(layouts).length === 0 ? generateDefaultLayouts() : layouts}
              onLayoutChange={handleLayoutChange}
              breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
              cols={{ lg: 5, md: 4, sm: 3, xs: 2, xxs: 1 }}
              isDraggable={false}
              isResizable={false}
              margin={[16, 16]}
              containerPadding={[0, 0]}
              rowHeight={60}
              autoSize={false}
            >
            {Object.keys(KPI_CARDS).map((key) => {
              if (!visibleCards[key]) return null;
              return (
                <div key={key}>
                  {cardRenderers[key] && cardRenderers[key]()}
                </div>
              );
            })}
            </ResponsiveGridLayout>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <h3 className="text-lg font-semibold mb-4">Key Performance Indicators</h3>
      
      {renderTimeFilter()}
      
      {renderCustomizeModal()}
      
      <div ref={gridRef} style={{ backgroundColor: '#ffffff', minHeight: '400px', padding: '16px' }}>
        <ResponsiveGridLayout
          className="layout"
          layouts={Object.keys(layouts).length === 0 ? generateDefaultLayouts() : layouts}
          onLayoutChange={handleLayoutChange}
          breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
          cols={{ lg: 5, md: 4, sm: 3, xs: 2, xxs: 1 }}
          isDraggable={true}
          isResizable={true}
          margin={[16, 16]}
          containerPadding={[0, 0]}
          draggableHandle=".drag-handle"
          rowHeight={60}
          autoSize={false}
        >
        {Object.keys(KPI_CARDS).map((key) => {
          if (!visibleCards[key]) return null;
          return (
            <div key={key}>
              {cardRenderers[key] && cardRenderers[key]()}
            </div>
          );
        })}
        </ResponsiveGridLayout>
      </div>
    </div>
  );
};

export default KPIsView;